import './Feed.sass';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { authSelector } from '$store/auth';
import { AuthRequired } from '../AuthRequired/AuthRequired';
import { Loader, MetaTags, StoriesList, UnitPhoto } from 'shared/components';
import { useCurrentUserInfo, useDesktop, useIsFirstRender, useTranslator } from '$hooks';
import { Header, PullToRefresh, Tabs, TabsItem } from '$uikit';
import { feedActions, feedSelector } from '$store/feed';
import { EXPLORE, FEED, MINE_EXPLORE, NOTIFY, SEARCH } from '$shared/constants/pages';
import router from '$navigation/router';
import { FeedNotification } from '$core/Feed/FeedNotification/FeedNotification';
import {
  contentPrioritySelector,
  genderFilterSelector,
  mainActions,
  mainSelector,
} from '$store/main';
import { ContentPriorityType } from '$store/main/interface';
import { FeedItems } from '$core/Feed/FeedItems/FeedItems';
import { ScrollView } from '$uikit/ScrollView/ScrollView';
import { IcSearch28, IcPlusCircle28, IcNotifications28 } from '$assets';
import { goTo, showModal, wasReturnedBack } from '$navigation/helper';
import { FeedGenderFilter } from '$core/Feed/FeedGenderFilter/FeedGenderFilter';
import { GenderFilterEnum } from '$store/models';
import { FeedBattle } from '$core/Feed/FeedBattle/FeedBattle';
import { getStorageItem } from '$utils';

export function Feed() {
  const { isLogged } = useSelector(authSelector);
  const {
    feed,
    notifications,
    promotedBattle,
    hasNewFollowingPosts,
    hasNewSponsorsPosts,
    activeSection,
  } = useSelector(feedSelector);
  const user = useCurrentUserInfo();
  const contentPriority = useSelector(contentPrioritySelector);
  const genderFilter = useSelector(genderFilterSelector);
  const isAdult = contentPriority === ContentPriorityType.ADULT;
  const isFirstRender = useIsFirstRender();
  const { decryptKeys, showInstallPrompt, mVerified, hasNotify } =
    useSelector(mainSelector);
  const isDesktop = useDesktop();
  const defaultTab = useMemo(() => {
    // if (isAdult) {
    //   let hasSponsorSubscription = false;
    //   for (let s in decryptKeys) {
    //     if (+s !== user.id && +s > 0) {
    //       hasSponsorSubscription = true;
    //       break;
    //     }
    //   }
    //
    //   if (decryptKeys[-1] || hasSponsorSubscription) {
    //     return 'smart';
    //   } else {
    //     return 'models';
    //   }
    // }

    return user?.isolated ? 'sponsored' : 'smart';
  }, [decryptKeys, isAdult, user]);

  const { route } = useRoute();
  const activeTab = useMemo(() => {
    if (isFirstRender && !!activeSection) {
      return activeSection;
    }
    return route.params?.section || defaultTab;
  }, [isFirstRender, activeSection, route.params?.section, defaultTab]);

  const { items, isLoadingMore, isLoading, nextFrom, sources, scrollOffset } =
    feed[activeTab] || {};

  const dispatch = useDispatch();
  const t = useTranslator();
  const isSuggestions = useMemo(() => {
    return ['suggestions', 'recent', 'popular'].includes(activeTab);
  }, [activeTab]);
  const isContentPriority = useMemo(() => {
    return contentPriority !== ContentPriorityType.NONE;
  }, [contentPriority]);
  const searchQuery = route.params.query || '';
  const isSearchTab = searchQuery.length >= 1;
  const scrollViewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    router.navigate(
      FEED,
      {
        ...route.params,
        section: isSearchTab ? 'search' : activeTab,
      },
      {
        replace: true,
      },
    );
  }, []);

  useEffect(() => {
    if (wasReturnedBack() && items) {
      return;
    }

    if (isLogged && isContentPriority && !items) {
      handleRefresh();
    }
  }, [activeTab, searchQuery, isLogged, genderFilter, contentPriority, items]);

  useEffect(() => {
    dispatch(feedActions.setActiveSection(isSearchTab ? 'smart' : activeTab));
  }, [activeTab]);

  const handleLoadMore = useCallback(() => {
    dispatch(
      feedActions.loadMore({
        section: isSearchTab ? 'search' : activeTab,
        query: searchQuery,
      }),
    );
  }, [searchQuery, activeTab]);

  const handleTabChange = (newTab) => {
    if (newTab === 'mine_cards') {
      goTo(MINE_EXPLORE);
      return;
    }

    if (newTab === 'explore') {
      router.navigate(EXPLORE);
      return;
    }

    const params: any = {};

    params.section = newTab;

    router.navigate(FEED, params);
  };

  useLayoutEffect(() => {
    scrollViewRef.current?.scrollTo(0, scrollOffset || 0);
  }, [scrollViewRef]);

  useEffect(() => {
    if (activeTab === 'search') {
      scrollViewRef.current?.scrollTo(0, 0);
    }
  }, [searchQuery, activeTab]);

  useLayoutEffect(() => {
    return () => {
      dispatch(
        feedActions.setScrollOffset({
          section: activeTab,
          scrollOffset: scrollViewRef.current?.scrollTop || 0,
        }),
      );
    };
  }, [scrollViewRef, activeTab]);

  useEffect(() => {
    if (isSearchTab) {
      handleRefresh();
    }

    return () => {
      dispatch(feedActions.clearSearch());
    };
  }, [searchQuery]);

  const handleRefresh = useCallback(() => {
    dispatch(
      feedActions.loadFeed({
        section: isSearchTab ? 'search' : activeTab,
        query: searchQuery,
      }),
    );
  }, [dispatch, activeTab, searchQuery]);

  const skipPwaBanner = getStorageItem('skipPwaBanner') === 'true';

  const activeSubTab = useMemo(() => {
    return activeTab;
  }, [activeTab]);

  const hasSponsorSubscription = useMemo(() => {
    for (const decryptKey in decryptKeys) {
      if (+decryptKey > 0 && +decryptKey !== user.id) {
        return true;
      }
    }

    return false;
  }, [decryptKeys]);

  function renderBattle() {
    if (activeTab === 'models') {
      return null;
    }

    if (activeTab === 'bundles') {
      return null;
    }

    if (!promotedBattle) {
      return null;
    }

    return <FeedBattle battle={promotedBattle} />;
  }

  function renderNotifications() {
    if (activeTab === 'models') {
      return null;
    }

    if (
      !notifications.length &&
      showInstallPrompt &&
      isDesktop &&
      !skipPwaBanner &&
      !window.isWebApp
    ) {
      return (
        <FeedNotification
          notification={{
            id: 'install_prompt',
            title: t('feed_app_notify_title'),
            description: t('feed_app_notify_caption'),
            iconUrl: '/assets/notifications/default.svg',
            actionText: t('feed_app_notify_btn'),
            actionUrl: '',
            action: '',
            actionParams: {},
          }}
        />
      );
    }

    if (!notifications.length) {
      return null;
    }

    return notifications.map((notification) => (
      <FeedNotification key={notification.id} notification={notification} />
    ));
  }

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      <MetaTags title={t('feed_title')} />
      <ScrollView
        onLoadMore={handleLoadMore}
        canLoadMore={!isLoading && !isLoadingMore && !!nextFrom}
        ref={scrollViewRef}
      >
        <Header
          leftContent={
            !isAdult || mVerified ? (
              <UnitPhoto
                hitSlop={12}
                className="Feed__my_profile_btn"
                size="extra-small"
                unit={user}
                onlineIndicator={false}
              />
            ) : null
          }
          rightIcons={[
            {
              visible: true,
              badge: hasNotify,
              icon: <IcNotifications28 />,
              onClick: () => {
                goTo(NOTIFY);
              },
            },
            {
              visible: isAdult && mVerified,
              icon: <IcPlusCircle28 />,
              onClick: () => dispatch(mainActions.setCreateMenuShown(true)),
            },
            {
              visible: isAdult && !mVerified,
              icon: <IcPlusCircle28 />,
              onClick: () => showModal('post'),
            },
          ]}
        >
          {window.isMedia ? 'TonPlace.io' : 'Ton.Place'}
        </Header>
        <div className="Feed__header_container">
          <div className="Feed__tabs">
            {!user?.isolated && (
              <Tabs selected={activeTab} onChange={handleTabChange} skipMargin>
                {/* @ts-ignore */}
                {/*<div className="Feed__mining_btn" value="mine_cards">*/}
                {/*  Hot Cards*/}
                {/*</div>*/}
                <TabsItem value="smart">{t('feed_tab_default')}</TabsItem>
                {hasSponsorSubscription && (
                  <TabsItem value="sponsored" badge={hasNewSponsorsPosts}>
                    {t('feed_tab_sponsored')}
                  </TabsItem>
                )}
                <TabsItem value="following" badge={hasNewFollowingPosts}>
                  {t('feed_tab_following')}
                </TabsItem>
                {/*{isAdult && (*/}
                {/*  <TabsItem value="bundles">{t('feed_section_bundles')}</TabsItem>*/}
                {/*)}*/}
                {!isAdult && <TabsItem value="replies">{t('feed_tab_replies')}</TabsItem>}
                {/*{contentPriority !== ContentPriorityType.ADULT && (*/}
                {/*  <TabsItem value="explore">{t('feed_tab_explore')}</TabsItem>*/}
                {/*)}*/}
                <TabsItem value="liked">{t('feed_tab_liked')}</TabsItem>
                {/*{contentPriority !== ContentPriorityType.ADULT && (*/}
                {/*  <TabsItem value="new">{t('feed_tab_new')}</TabsItem>*/}
                {/*)}*/}
              </Tabs>
            )}
          </div>
        </div>

        <PullToRefresh isRefreshing={isLoading} onRefresh={handleRefresh}>
          {renderBattle()}
          {renderNotifications()}
          {(!isLoading || items?.length > 0) && activeTab !== 'models' && <StoriesList />}

          {!isLoading &&
            activeTab !== 'models' &&
            genderFilter === GenderFilterEnum.NONE &&
            isAdult && <FeedGenderFilter />}
          {/*{!isLoading && isAdult && activeTab === 'models' && defaultTab === 'models' && (*/}
          {/*  <AdultWelcome />*/}
          {/*)}*/}
          {/*{!isLoading && !isAdult && !welcomeHidden && <WelcomeBlock />}*/}
          <FeedItems
            sources={sources}
            isSuggestions={isSuggestions}
            feed={items}
            activeTab={activeTab}
            isContentPriority={isContentPriority}
            isLoading={isLoading}
            isAdult={isAdult}
            isMoreItems={!!nextFrom}
          />
          {isLoadingMore && <Loader mode="additional" />}
        </PullToRefresh>
      </ScrollView>
    </>
  );
}
